import { z } from 'zod';
import { normalizeReferenceText } from './normalize.js';
import type { ReferenceProduct } from './types.js';

const requiredText = z.string().trim().min(1, 'Required');

const optionalText = z
  .string()
  .trim()
  .nullable()
  .optional()
  .transform((value) => value || null);

const optionalTextList = z
  .array(z.string())
  .nullable()
  .optional()
  .transform((values) =>
    (values ?? []).map((value) => value.trim()).filter(Boolean),
  );

/** Runtime boundary for one source-agnostic curated reference record. */
const referenceProductImportSchema = z.object({
  referenceId: requiredText,
  brand: requiredText,
  productName: requiredText,
  strainName: optionalText,
  classification: optionalText,
  lineage: optionalText,
  terpenes: optionalTextList,
  aliases: optionalTextList,
});

export interface ReferenceImportValidationIssue {
  readonly path: readonly (string | number)[];
  readonly message: string;
}

export interface RejectedReferenceImportRecord {
  /** Null means the top-level input itself was not an array. */
  readonly index: number | null;
  readonly input: unknown;
  readonly issues: readonly ReferenceImportValidationIssue[];
}

export type ReferenceImportConflictCode =
  | 'duplicate_reference_id'
  | 'duplicate_commercial_identity';

export interface ReferenceImportConflict {
  readonly code: ReferenceImportConflictCode;
  /** All valid input records participating in this conflict, in input order. */
  readonly recordIndices: readonly number[];
  readonly message: string;
}

export interface ReferenceProductImportResult {
  /** Valid, non-conflicting products in their original input order. */
  readonly accepted: readonly ReferenceProduct[];
  /** Records that failed runtime field validation. */
  readonly rejected: readonly RejectedReferenceImportRecord[];
  /** Batch-level duplicate ID and commercial-identity conflicts. */
  readonly conflicts: readonly ReferenceImportConflict[];
}

interface ParsedRecord {
  index: number;
  product: ReferenceProduct;
}

function freezeProduct(
  value: z.output<typeof referenceProductImportSchema>,
): ReferenceProduct {
  return Object.freeze({
    ...value,
    terpenes: Object.freeze([...value.terpenes]),
    aliases: Object.freeze([...value.aliases]),
  });
}

function findConflictGroups(
  records: readonly ParsedRecord[],
  code: ReferenceImportConflictCode,
  keyFor: (product: ReferenceProduct) => string | null,
): ReferenceImportConflict[] {
  const groups = new Map<string, number[]>();

  for (const record of records) {
    const key = keyFor(record.product);
    if (key === null) continue;

    const indices = groups.get(key);
    if (indices) indices.push(record.index);
    else groups.set(key, [record.index]);
  }

  return [...groups.values()]
    .filter((indices) => indices.length > 1)
    .map((recordIndices) =>
      Object.freeze({
        code,
        recordIndices: Object.freeze(recordIndices),
        message:
          code === 'duplicate_reference_id'
            ? 'Multiple records have the same referenceId'
            : 'Multiple records have the same normalized brand and productName',
      }),
    );
}

/**
 * Validate and import an untrusted batch of curated reference-product data.
 * Ordinary invalid records and batch conflicts are returned, never thrown.
 */
export function importReferenceProducts(
  input: unknown,
): ReferenceProductImportResult {
  if (!Array.isArray(input)) {
    return Object.freeze({
      accepted: Object.freeze([]),
      rejected: Object.freeze([
        Object.freeze({
          index: null,
          input,
          issues: Object.freeze([
            Object.freeze({ path: Object.freeze([]), message: 'Expected an array' }),
          ]),
        }),
      ]),
      conflicts: Object.freeze([]),
    });
  }

  const parsed: ParsedRecord[] = [];
  const rejected: RejectedReferenceImportRecord[] = [];

  for (let index = 0; index < input.length; index += 1) {
    const record: unknown = input[index];
    const result = referenceProductImportSchema.safeParse(record);
    if (result.success) {
      parsed.push({ index, product: freezeProduct(result.data) });
      continue;
    }

    rejected.push(
      Object.freeze({
        index,
        input: record,
        issues: Object.freeze(
          result.error.issues.map((issue) =>
            Object.freeze({
              path: Object.freeze([...issue.path]),
              message: issue.message,
            }),
          ),
        ),
      }),
    );
  }

  const conflicts = [
    ...findConflictGroups(parsed, 'duplicate_reference_id', ({ referenceId }) =>
      referenceId,
    ),
    ...findConflictGroups(
      parsed,
      'duplicate_commercial_identity',
      ({ brand, productName }) => {
        const normalizedBrand = normalizeReferenceText(brand);
        const normalizedProductName = normalizeReferenceText(productName);

        return normalizedBrand && normalizedProductName
          ? `${normalizedBrand}\u0000${normalizedProductName}`
          : null;
      },
    ),
  ];
  const conflictingIndices = new Set(
    conflicts.flatMap(({ recordIndices }) => recordIndices),
  );

  return Object.freeze({
    accepted: Object.freeze(
      parsed
        .filter(({ index }) => !conflictingIndices.has(index))
        .map(({ product }) => product),
    ),
    rejected: Object.freeze(rejected),
    conflicts: Object.freeze(conflicts),
  });
}
